import React, { useContext, useEffect, useRef } from "react"
import {
    View,
    StyleSheet,
    TouchableOpacity,
    Image,
    Animated,
    Easing,
} from "react-native"
import { theme } from "@common/theme/theme"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import logo from "@assets/logo-blue.png"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { WIDE_BREAKPOINT } from "./FunnelQuestionScreen"

/**
 * L'en-tête d'étape des funnels à scènes (stage, Rentrée).
 *
 * Il diffère volontairement de `BrevetFunnelHeader` sur deux points, et c'est
 * tout le sujet :
 *
 * 1. **Pas de compteur « 3/7 »**. Un chiffre sur un écran de vente annonce le
 *    travail qui reste, pas le chemin parcouru — et il fait formulaire
 *    administratif. Les segments disent la même chose sans la mettre en mots.
 * 2. **Une progression segmentée, pas une jauge**. Une barre continue de 4 px
 *    posée sur un liseré gris est le vocabulaire d'un installeur ; des segments
 *    fins et espacés sont celui d'un produit soigné. Le segment qui vient d'être
 *    franchi se remplit de gauche à droite à l'arrivée sur l'écran : la
 *    progression se voit se faire.
 *
 * L'en-tête n'a pas de bordure basse. La séparation se fait par le blanc.
 */
const FunnelStepHeader = ({
    step,
    total,
    onBack,
    hideBack = false,
    hideLogo = false,
}) => {
    const current = Math.max(1, Math.min(total, step))
    // Sur grand écran, le panneau de gauche porte déjà le logo : l'en-tête ne
    // le répète pas. Sur mobile, c'est lui la seule marque de l'écran.
    const dimensions = useContext(DimensionsContext)
    const showLogo = !hideLogo && (dimensions?.width ?? 0) < WIDE_BREAKPOINT

    // Seul le segment courant s'anime ; les précédents sont déjà acquis.
    const grow = useRef(new Animated.Value(0)).current
    useEffect(() => {
        grow.setValue(0)
        Animated.timing(grow, {
            toValue: 1,
            duration: 460,
            delay: 90,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
        }).start()
    }, [current])

    const width = grow.interpolate({
        inputRange: [0, 1],
        outputRange: ["0%", "100%"],
    })

    return (
        <View style={styles.container}>
            <View style={styles.topRow}>
                {!hideBack ? (
                    <TouchableOpacity
                        onPress={onBack}
                        style={styles.back}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                        accessibilityRole="button"
                        accessibilityLabel="Revenir à l'étape précédente"
                    >
                        <FontAwesome5
                            name="chevron-left"
                            size={13}
                            color={theme.colors.primary}
                        />
                    </TouchableOpacity>
                ) : null}
                {showLogo ? (
                    <Image
                        source={logo}
                        style={styles.logo}
                        resizeMode="contain"
                    />
                ) : null}
            </View>

            <View
                style={styles.segments}
                accessibilityRole="progressbar"
                accessibilityValue={{ min: 1, max: total, now: current }}
            >
                {Array.from({ length: total }, (_, i) => (
                    <View key={i} style={styles.segment}>
                        {i < current - 1 ? (
                            <View style={styles.segmentDone} />
                        ) : null}
                        {i === current - 1 ? (
                            <Animated.View
                                style={[styles.segmentDone, { width }]}
                            />
                        ) : null}
                    </View>
                ))}
            </View>
        </View>
    )
}

const SEGMENT_H = 3

const styles = StyleSheet.create({
    container: { paddingTop: 10, paddingBottom: 14 },
    topRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        minHeight: 24,
    },
    // Une cible tactile large autour d'un chevron discret : le retour existe,
    // il ne réclame pas l'attention.
    back: {
        width: 24,
        height: 24,
        alignItems: "center",
        justifyContent: "center",
    },
    logo: { width: 72, height: 20 },
    segments: { flexDirection: "row", gap: 5, marginTop: 12 },
    segment: {
        flex: 1,
        height: SEGMENT_H,
        borderRadius: SEGMENT_H,
        backgroundColor: "rgba(34,63,143,0.13)",
        overflow: "hidden",
    },
    segmentDone: {
        height: "100%",
        width: "100%",
        borderRadius: SEGMENT_H,
        backgroundColor: theme.colors.primary,
    },
})

export default FunnelStepHeader
